/**
 * Service Worker Composable
 * Manages PWA service worker updates and offline status
 */

import { ref, onMounted, onUnmounted } from 'vue';
import { useRegisterSW } from 'virtual:pwa-register/vue';

/**
 * Handle service worker errors by unregistering and reloading (with loop guard)
 * This fixes issues when old SWs try to precache files that no longer exist
 */
async function handleSWError() {
  // Guard against infinite reload loops - only allow one recovery attempt per 30s
  const RECOVERY_KEY = 'sw-recovery-timestamp';
  const lastRecovery = sessionStorage.getItem(RECOVERY_KEY);
  const now = Date.now();

  if (lastRecovery && (now - parseInt(lastRecovery, 10)) < 30000) {
    console.warn('[SW] Recovery already attempted recently, skipping reload to prevent loop');
    return;
  }

  sessionStorage.setItem(RECOVERY_KEY, now.toString());

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
    // Clear caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    // Reload to get fresh SW
    window.location.reload();
  }
}

// Listen for SW errors globally (catches precache failures)
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.name === 'bad-precaching-response' ||
        event.reason?.message?.includes('bad-precaching-response')) {
      console.warn('[SW] Precache failure detected, clearing and reloading...');
      event.preventDefault();
      handleSWError();
    }
  });
}

/**
 * Composable for managing service worker state and updates
 * @returns {Object} Service worker state and methods
 */
export function useServiceWorker() {
  const isOnline = ref(navigator.onLine);
  const registrationError = ref(null);

  // Register service worker with vite-plugin-pwa (autoUpdate mode)
  const {
    offlineReady,
    needRefresh,
    updateServiceWorker
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      console.log('[SW] Service worker registered:', swUrl);

      // Check for updates periodically (every hour)
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('[SW] Registration error:', error);
      registrationError.value = error;

      // If registration fails due to precache issues, try to recover
      if (error?.message?.includes('precach') || error?.name === 'bad-precaching-response') {
        handleSWError();
      }
    }
  });

  // Handle online/offline status
  function handleOnline() {
    isOnline.value = true;
  }

  function handleOffline() {
    isOnline.value = false;
  }

  onMounted(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  });

  onUnmounted(() => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  });

  /**
   * Manually check for updates
   */
  async function checkForUpdates() {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
      }
    }
  }

  return {
    // State
    isOnline,
    isOfflineReady: offlineReady,
    isUpdateAvailable: needRefresh,
    registrationError,

    // Methods
    checkForUpdates,
    updateServiceWorker
  };
}

/**
 * Simple hook for checking online status without full SW features
 */
export function useOnlineStatus() {
  const isOnline = ref(navigator.onLine);

  function handleOnline() {
    isOnline.value = true;
  }

  function handleOffline() {
    isOnline.value = false;
  }

  onMounted(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  });

  onUnmounted(() => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  });

  return { isOnline };
}
